// @ts-nocheck
/**
 * Annotation Layer Component
 * Handles real-time annotations and collaborative markup
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button, TextInput, TextArea, Modal, Tooltip, Tag, OverflowMenu, OverflowMenuItem } from '@carbon/react';
import { Add, Edit, TrashCan, Chat, TextHighlight, Help, Idea } from '@carbon/react/icons';

import { useCollaboration } from '../../hooks/useCollaboration';
import type { Annotation } from '../../services/collaborationWebSocketService';

export interface AnnotationLayerProps {
  collaboration: ReturnType<typeof useCollaboration>;
  children: React.ReactNode;
  className?: string;
}

interface AnnotationMarkerProps {
  annotation: Annotation;
  onClick: () => void;
  onEdit: () => void;
  onDelete: () => void;
  canEdit: boolean;
}

const AnnotationMarker: React.FC<AnnotationMarkerProps> = ({ annotation, onClick, onEdit, onDelete, canEdit }) => {
  const getIcon = () => {
    switch (annotation.type) {
      case 'note':
        return <Chat size={16} />;
      case 'highlight':
        return <TextHighlight size={16} />;
      case 'question':
        return <Help size={16} />;
      case 'suggestion':
        return <Idea size={16} />;
      default:
        return <Chat size={16} />;
    }
  };

  const getColor = () => {
    return annotation.color || '#0f62fe';
  };

  return (
    <div
      className="annotation-layer__marker"
      style={{
        position: 'absolute',
        left: `${annotation.position.x}px`,
        top: `${annotation.position.y}px`,
        zIndex: 1001,
      }}
    >
      <Tooltip content={`${annotation.type} by ${annotation.userName}`}>
        <Button
          kind="ghost"
          size="sm"
          renderIcon={getIcon}
          onClick={onClick}
          style={{
            backgroundColor: getColor(),
            color: '#ffffff',
            minHeight: '24px',
            width: '24px',
            padding: '0',
          }}
        />
      </Tooltip>

      {canEdit && (
        <div className="annotation-layer__marker-actions">
          <OverflowMenu ariaLabel="Annotation actions" iconDescription="More actions" size="sm">
            <OverflowMenuItem itemText="Edit" onClick={onEdit} />
            <OverflowMenuItem itemText="Delete" onClick={onDelete} requireConfirmation />
          </OverflowMenu>
        </div>
      )}
    </div>
  );
};

export const AnnotationLayer: React.FC<AnnotationLayerProps> = ({ collaboration, children, className = '' }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showAnnotationModal, setShowAnnotationModal] = useState(false);
  const [selectedAnnotation, setSelectedAnnotation] = useState<Annotation | null>(null);
  const [annotationPosition, setAnnotationPosition] = useState<{ x: number; y: number } | null>(null);
  const [annotationType, setAnnotationType] = useState<'note' | 'highlight' | 'question' | 'suggestion'>('note');
  const [annotationContent, setAnnotationContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const { createAnnotation, updateAnnotation, deleteAnnotation, hasCapability, annotations } = collaboration;

  // Handle right-click to create annotation
  const handleContextMenu = useCallback(
    (event: MouseEvent) => {
      if (!hasCapability('annotations')) return;

      event.preventDefault();

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      setAnnotationPosition({ x, y });
      setAnnotationContent('');
      setAnnotationType('note');
      setIsEditing(false);
      setSelectedAnnotation(null);
      setShowAnnotationModal(true);
    },
    [hasCapability]
  );

  // Handle annotation creation
  const handleCreateAnnotation = useCallback(() => {
    if (!annotationPosition || !annotationContent.trim()) return;

    const elementUnderPosition = document.elementFromPoint(
      annotationPosition.x + (containerRef.current?.getBoundingClientRect().left || 0),
      annotationPosition.y + (containerRef.current?.getBoundingClientRect().top || 0)
    );

    const elementId =
      elementUnderPosition?.id || elementUnderPosition?.getAttribute('data-element-id') || 'annotation-layer';

    createAnnotation({
      type: annotationType,
      elementId,
      position: annotationPosition,
      content: annotationContent.trim(),
      color: getAnnotationColor(annotationType),
    });

    setShowAnnotationModal(false);
    setAnnotationPosition(null);
    setAnnotationContent('');
  }, [annotationPosition, annotationContent, annotationType, createAnnotation]);

  // Handle annotation editing
  const handleEditAnnotation = useCallback((annotation: Annotation) => {
    setSelectedAnnotation(annotation);
    setAnnotationContent(annotation.content);
    setAnnotationType(annotation.type);
    setIsEditing(true);
    setShowAnnotationModal(true);
  }, []);

  // Handle annotation update
  const handleUpdateAnnotation = useCallback(() => {
    if (!selectedAnnotation || !annotationContent.trim()) return;

    updateAnnotation(selectedAnnotation.annotationId, {
      content: annotationContent.trim(),
      type: annotationType,
      color: getAnnotationColor(annotationType),
    });

    setShowAnnotationModal(false);
    setSelectedAnnotation(null);
    setAnnotationContent('');
    setIsEditing(false);
  }, [selectedAnnotation, annotationContent, annotationType, updateAnnotation]);

  // Handle annotation deletion
  const handleDeleteAnnotation = useCallback(
    (annotationId: string) => {
      deleteAnnotation(annotationId);
    },
    [deleteAnnotation]
  );

  // Get annotation color based on type
  const getAnnotationColor = (type: string) => {
    switch (type) {
      case 'note':
        return '#0f62fe';
      case 'highlight':
        return '#fdd13a';
      case 'question':
        return '#da1e28';
      case 'suggestion':
        return '#24a148';
      default:
        return '#0f62fe';
    }
  };

  // Set up context menu listener
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !hasCapability('annotations')) return;

    container.addEventListener('contextmenu', handleContextMenu);

    return () => {
      container.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [handleContextMenu, hasCapability]);

  return (
    <div
      ref={containerRef}
      className={`annotation-layer ${className}`}
      style={{ position: 'relative', width: '100%', height: '100%' }}
    >
      {children}

      {/* Render annotation markers */}
      {hasCapability('annotations') &&
        Array.from(annotations.entries()).map(([annotationId, annotation]) => (
          <AnnotationMarker
            key={annotationId}
            annotation={annotation}
            onClick={() => {
              setSelectedAnnotation(annotation);
              setShowAnnotationModal(true);
              setIsEditing(false);
            }}
            onEdit={() => handleEditAnnotation(annotation)}
            onDelete={() => handleDeleteAnnotation(annotationId)}
            canEdit={
              annotation.userId ===
              collaboration.sessionState?.participants.find(p => p.userId === annotation.userId)?.userId
            }
          />
        ))}

      {/* Annotation Modal */}
      <Modal
        open={showAnnotationModal}
        onRequestClose={() => {
          setShowAnnotationModal(false);
          setSelectedAnnotation(null);
          setAnnotationPosition(null);
          setIsEditing(false);
        }}
        modalHeading={isEditing ? 'Edit Annotation' : selectedAnnotation ? 'View Annotation' : 'Create Annotation'}
        modalLabel="Collaboration"
        primaryButtonText={isEditing ? 'Update' : selectedAnnotation ? 'Close' : 'Create'}
        secondaryButtonText={selectedAnnotation && !isEditing ? 'Edit' : 'Cancel'}
        onRequestSubmit={isEditing ? handleUpdateAnnotation : selectedAnnotation ? undefined : handleCreateAnnotation}
        onSecondarySubmit={
          selectedAnnotation && !isEditing ? () => handleEditAnnotation(selectedAnnotation) : undefined
        }
        preventCloseOnClickOutside
      >
        <div className="annotation-layer__modal-content">
          {/* Annotation Type Selection */}
          {(!selectedAnnotation || isEditing) && (
            <div className="annotation-layer__type-selection">
              <fieldset className="cds--fieldset">
                <legend className="cds--label">Annotation Type</legend>
                <div className="cds--radio-button-group">
                  {[
                    { value: 'note', label: 'Note', icon: <Chat size={16} /> },
                    { value: 'highlight', label: 'Highlight', icon: <TextHighlight size={16} /> },
                    { value: 'question', label: 'Question', icon: <Help size={16} /> },
                    { value: 'suggestion', label: 'Suggestion', icon: <Idea size={16} /> },
                  ].map(type => (
                    <div key={type.value} className="cds--radio-button-wrapper">
                      <input
                        type="radio"
                        className="cds--radio-button"
                        id={`annotation-type-${type.value}`}
                        name="annotation-type"
                        value={type.value}
                        checked={annotationType === type.value}
                        onChange={e => setAnnotationType(e.target.value as any)}
                      />
                      <label htmlFor={`annotation-type-${type.value}`} className="cds--radio-button__label">
                        <span className="cds--radio-button__appearance"></span>
                        <span className="cds--radio-button__label-text">
                          {type.icon} {type.label}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              </fieldset>
            </div>
          )}

          {/* Annotation Content */}
          <div className="annotation-layer__content-input">
            {selectedAnnotation && !isEditing ? (
              <div className="annotation-layer__view-mode">
                <div className="annotation-layer__annotation-meta">
                  <Tag type="blue" size="sm">
                    {selectedAnnotation.type}
                  </Tag>
                  <span>by {selectedAnnotation.userName}</span>
                  <span>{new Date(selectedAnnotation.timestamp).toLocaleString()}</span>
                </div>
                <div className="annotation-layer__annotation-content">{selectedAnnotation.content}</div>
              </div>
            ) : (
              <TextArea
                id="annotation-content"
                labelText="Annotation Content"
                helperText="Describe your note, question, or suggestion"
                value={annotationContent}
                onChange={e => setAnnotationContent(e.target.value)}
                rows={4}
                placeholder={`Enter your ${annotationType} here...`}
              />
            )}
          </div>

          {/* Position Info */}
          {annotationPosition && (
            <div className="annotation-layer__position-info">
              <small>
                Position: ({annotationPosition.x}, {annotationPosition.y})
              </small>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AnnotationLayer;
